import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

// Graduation criteria
const GRADUATION_CRITERIA = {
  toIntermediate: {
    wizardStepsCompleted: 5,
    dealsCreated: 3,
    callsMade: 2,
    leadsProcessed: 10,
  },
  toAdvanced: {
    wizardStepsCompleted: 9,
    dealsCreated: 10,
    callsMade: 10,
    leadsProcessed: 50,
  },
};

export function useGuidanceGraduation() {
  const { user, profile, refetchProfile } = useAuth();

  const checkAndGraduate = useCallback(async () => {
    if (!user || !profile?.tenant_id) return;
    
    const currentLevel = (profile.guidance_level || 'beginner') as 'beginner' | 'intermediate' | 'advanced';
    
    // Already at max level
    if (currentLevel === 'advanced') return;

    try {
      // Fetch user activity metrics
      const [wizardResult, dealsResult, callsResult, leadsResult] = await Promise.all([
        supabase
          .from('wizard_progress')
          .select('completed_steps')
          .eq('user_id', user.id)
          .maybeSingle(),
        supabase
          .from('deals')
          .select('id', { count: 'exact', head: true })
          .eq('tenant_id', profile.tenant_id),
        supabase
          .from('calls')
          .select('id', { count: 'exact', head: true })
          .eq('tenant_id', profile.tenant_id),
        supabase
          .from('leads')
          .select('id', { count: 'exact', head: true })
          .eq('tenant_id', profile.tenant_id)
          .neq('status', 'new'),
      ]);

      const wizardStepsCompleted = (wizardResult.data?.completed_steps || []).length;
      const dealsCreated = dealsResult.count || 0;
      const callsMade = callsResult.count || 0;
      const leadsProcessed = leadsResult.count || 0;

      let newLevel: 'beginner' | 'intermediate' | 'advanced' = currentLevel;

      // Check graduation to intermediate
      if (currentLevel === 'beginner') {
        const criteria = GRADUATION_CRITERIA.toIntermediate;
        const meetsWizard = wizardStepsCompleted >= criteria.wizardStepsCompleted;
        const meetsDeals = dealsCreated >= criteria.dealsCreated;
        const meetsCalls = callsMade >= criteria.callsMade;
        const meetsLeads = leadsProcessed >= criteria.leadsProcessed;
        
        // Graduate if meets at least 2 criteria
        const criteriaMet = [meetsWizard, meetsDeals, meetsCalls, meetsLeads].filter(Boolean).length;
        if (criteriaMet >= 2) {
          newLevel = 'intermediate';
        }
      }

      // Check graduation to advanced
      if (currentLevel === 'intermediate' || newLevel === 'intermediate') {
        const criteria = GRADUATION_CRITERIA.toAdvanced;
        const meetsWizard = wizardStepsCompleted >= criteria.wizardStepsCompleted;
        const meetsDeals = dealsCreated >= criteria.dealsCreated;
        const meetsCalls = callsMade >= criteria.callsMade;
        const meetsLeads = leadsProcessed >= criteria.leadsProcessed;
        
        // Graduate if meets at least 3 criteria
        const criteriaMet = [meetsWizard, meetsDeals, meetsCalls, meetsLeads].filter(Boolean).length;
        if (criteriaMet >= 3) {
          newLevel = 'advanced';
        }
      }

      // Update if level changed
      if (newLevel !== currentLevel) {
        const { error } = await supabase
          .from('profiles')
          .update({ guidance_level: newLevel })
          .eq('id', user.id);

        if (!error) {
          refetchProfile?.();
        }
      }
    } catch {
      // Silently handle graduation check errors
    }
  }, [user, profile, refetchProfile]);

  // Check graduation on mount and when profile changes
  useEffect(() => {
    if (user && profile) {
      // Delay check to avoid blocking initial render
      const timer = setTimeout(checkAndGraduate, 2000);
      return () => clearTimeout(timer);
    }
  }, [user, profile?.id, checkAndGraduate]);

  return { checkAndGraduate };
}
